import { useMemo } from 'react';
import {
  useReactTable, getCoreRowModel, flexRender,
  type ColumnDef, type RowSelectionState, type OnChangeFn,
} from '@tanstack/react-table';
import { Skeleton } from '@heroui/react';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/cn';
import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon } from '@/icons';

/** Column meta we read for server-side sorting + cell styling. */
export interface ColumnMetaExt {
  sortKey?: string;
  className?: string;
  headerClassName?: string;
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  getRowId: (row: TData) => string;
  isLoading?: boolean;
  /** Row selection (controlled) for bulk actions. */
  enableSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** Server sort encoded as 'field' / '-field'; onSort toggles a field. */
  sort?: string;
  onSort?: (field: string) => void;
  onRowClick?: (row: TData) => void;
  emptyMessage?: string;
  skeletonRows?: number;
  /** Render an error surface (with retry) instead of rows when a query fails. */
  isError?: boolean;
  /** The query error, so the error row can name the kind of failure (unavailable, access denied, ...). */
  error?: unknown;
  onRetry?: () => void;
}

/**
 * Generic, server-driven table over TanStack Table. It renders + manages row
 * selection only; pagination, sorting and filtering are driven by the URL state
 * (the parent passes data + sort and handles onSort). Reused by every list view.
 */
export function DataTable<TData>({
  columns, data, getRowId, isLoading = false,
  enableSelection = false, rowSelection = {}, onRowSelectionChange,
  sort = '', onSort, onRowClick, emptyMessage = 'No results.', skeletonRows = 8,
  isError = false, error, onRetry,
}: DataTableProps<TData>) {
  const allColumns = useMemo<ColumnDef<TData, unknown>[]>(() => {
    if (!enableSelection) return columns;
    const selectCol: ColumnDef<TData, unknown> = {
      id: '_select',
      size: 40,
      header: ({ table }) => (
        <input
          type="checkbox"
          aria-label="Select all rows"
          className="h-4 w-4 cursor-pointer accent-brand"
          checked={table.getIsAllRowsSelected()}
          ref={(el) => { if (el) el.indeterminate = table.getIsSomeRowsSelected(); }}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          aria-label="Select row"
          className="h-4 w-4 cursor-pointer accent-brand"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    };
    return [selectCol, ...columns];
  }, [columns, enableSelection]);

  const table = useReactTable({
    data,
    columns: allColumns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: enableSelection,
    state: { rowSelection },
    onRowSelectionChange,
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-border">
              {hg.headers.map((header) => {
                const meta = header.column.columnDef.meta as ColumnMetaExt | undefined;
                const sortable = !!meta?.sortKey && !!onSort;
                const active = meta?.sortKey && (sort === meta.sortKey || sort === `-${meta.sortKey}`);
                const desc = meta?.sortKey && sort === `-${meta.sortKey}`;
                return (
                  <th
                    key={header.id}
                    className={cn(
                      'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-fg-subtle',
                      meta?.headerClassName,
                    )}
                  >
                    {header.isPlaceholder ? null : sortable ? (
                      <button
                        type="button"
                        onClick={() => onSort!(meta!.sortKey!)}
                        className="inline-flex items-center gap-1 hover:text-fg"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {active
                          ? (desc ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronUpIcon className="h-3.5 w-3.5" />)
                          : <ChevronUpDownIcon className="h-3.5 w-3.5 text-fg-subtle" />}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {isLoading ? (
            Array.from({ length: skeletonRows }).map((_, i) => (
              <tr key={`sk-${i}`} className="border-b border-border">
                {allColumns.map((_, j) => (
                  <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full rounded" /></td>
                ))}
              </tr>
            ))
          ) : isError ? (
            <tr>
              <td colSpan={allColumns.length} className="px-4 py-8">
                <ErrorState error={error} onRetry={onRetry} compact />
              </td>
            </tr>
          ) : table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={allColumns.length} className="px-4 py-12 text-center text-fg-muted">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row.original); } } : undefined}
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                className={cn(
                  'border-b border-border transition-colors hover:bg-surface-raised/50 focus-visible:bg-brand/5 focus-visible:outline-none',
                  onRowClick && 'cursor-pointer',
                  row.getIsSelected() && 'bg-brand/5',
                )}
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta as ColumnMetaExt | undefined;
                  return (
                    <td key={cell.id} className={cn('px-4 py-3 text-fg', meta?.className)}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
